import { Link, useNavigate } from "react-router-dom";
import PortfolioIcon from '../assets/logos/my portfolio.svg';
import LabAssignmentIcon from '../assets/logos/lab assignment.svg';
import EvaluateIcon from '../assets/logos/evaluate icon.svg';
import ManageMenuIcon from '../assets/logos/manage menu icon.svg';
import VectorIcon from '../assets/logos/Vector.svg';
import { QuestionCircleOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faCloudSun, faMoon } from '@fortawesome/free-solid-svg-icons';
import { Tour, message, Skeleton, Modal } from "antd";
import { Alert, Row, Col } from "antd";
import { global_var, projectCategoryMapping, categoryDisplayNames } from "../services/cs-lab-config";
import {
  getIdeProjectLogo,
  getCurrentGreetingMessage,
  getIdeProjectTypeMetaData,
  getShowMyProjectFlag,
  getShowTutorialVideo,
  trackEvent,
} from "./utils/util-functions";
import { getProgrammingTip, loadMenuForCslab, getPendingEvaluationCount } from "../services/ide-apis";
import { getFromCache, setCache } from "../services/cache-service";

import { useState, useEffect, useLayoutEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import tourIDEimg from "../assets/images/tourIDEimg.png";
import tourProjects from "../assets/images/tourProjects.png";
import AiBand from "./common/ai-band";
import { canCreateProject } from "../services/subscription-gate-service";
import ChatAiIconBlack from '../assets/logos/chat-ai-icon-black.svg';
import "./home.css";

// line icons for the New Project / My Projects / Video Tutorial buttons
const lineIcon = (strokeWidth, children) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" width="18" height="18">{children}</svg>
);
const NewIcon = () => lineIcon(2.5, <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>);
const OpenIcon = () => lineIcon(2, <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />);
const PlayIcon = () => lineIcon(2, <polygon points="5 3 19 12 5 21 5 3" />);

// Lab cards hidden on the home page when opened with ?platform=smart_board
const SMART_BOARD_UNAVAILABLE_FEATURES = [
  'Google Apps',
  'MakeCode',
  'MIT App Inventor',
  'Typing Games',
];

const SMART_BOARD_HIDDEN_STACKS = [
  // Google apps
  'gdocs', 'gsheets', 'gslides', 'gforms',
  'makecode',
  'mit_app_inventor', 'mit_app_inventor_2',
  // Typing games
  'dance_mat_typing', 'typing_ninja', 'web_typist',
];

// Remembered across Home unmount/mount so Back returns to where the user left off
let lastHomeScrollY = 0;

function buildProjectStacks(res) {
  const response = res.data;
  if (response.statusCode !== 200) return null;

  const updatedProjectStacks = {};
  response.menuStructure.forEach(item => {
    const categoryName = item.categoryName;
    const menuName = item.stackName;

    if (global_var.platform === 'smart_board' && SMART_BOARD_HIDDEN_STACKS.includes(menuName)) {
      return;
    }

    if (!updatedProjectStacks[categoryName]) {
      updatedProjectStacks[categoryName] = { sortOrder: projectCategoryMapping[categoryName]['sortOrder'], items: [] };
    }

    updatedProjectStacks[categoryName].items.push({ projectStackName: menuName });
  });

  if (response.aiSettings) {
    global_var.allowedAiFeatures = response.aiSettings;
  }

  const sortedObj = {};
  Object.entries(updatedProjectStacks)
    .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
    .forEach(([key, value]) => {
      sortedObj[key] = value.items;
    });
  return sortedObj;
}

// category -> the animated card shown under its heading
const AI_BAND_VARIANTS = { "Programming Languages": "python", "Visual Coding": "block" };

// The Quick Tips card, shown as the last card of the Programming Languages row.
const TIP_ITEM = { isTip: true };

// header colour of each tool card; stacks not listed here fall back to HEAD_FALLBACK by position
const HEAD_GRADIENTS = {
  blue: "linear-gradient(135deg,#2563EB,#1d4ed8)",
  red: "linear-gradient(135deg,#DC2626,#b91c1c)",
  amber: "linear-gradient(135deg,#D97706,#b45309)",
  green: "linear-gradient(135deg,#059669,#047857)",
  purple: "linear-gradient(135deg,#7C3AED,#6d28d9)",
  dark: "linear-gradient(135deg,#1F2937,#111827)",
  // colours taken from each tool's logo
  indigo: "linear-gradient(135deg,#6366F1,#4338CA)", // CS Typing Tutor (lavender keyboard)
  orange: "linear-gradient(135deg,#F97316,#C2410C)", // MakeCode
  navy: "linear-gradient(135deg,#1E4A6B,#0F2335)", // CS Flow & Circuit (navy + teal)
  teal: "linear-gradient(135deg,#00979D,#006B70)", // Arduino
};
const STACK_HEAD = {
  python: "blue", html_css: "red", scratch: "amber", vc3: "amber", scratchjr: "green",
  mit_app_inventor: "purple", mit_app_inventor_2: "purple", sql2: "red", codeCombat_game: "green",
  geekyBird_game: "blue", geekyBird_game_programmable: "amber", typing_ninja: "dark", web_typist: "indigo",
  makecode: "orange", circuit: "navy", arduino: "teal",
};
// these logos have a lot of empty margin in their image, so they get a zoomed-in fit (see home.css)
const ZOOMED_LOGOS = { python: "logo-python", circuit: "logo-circuit" };

const HEAD_FALLBACK = ["blue", "red", "amber", "green", "purple", "dark"];

const getHeadBackground = (stackName, index) =>
  HEAD_GRADIENTS[STACK_HEAD[stackName] || HEAD_FALLBACK[index % HEAD_FALLBACK.length]];

const QuickTipCard = ({ tip }) => (
  <div className="tips-card">
    <div className="tip-icon">💡</div>
    <div className="tip-title">Quick Tips{tip ? "!" : ""}</div>
    {tip
      ? <ReactMarkdown className="tip-text" children={tip} />
      : <Skeleton active="true" />}
  </div>
);

const ActionCard = ({ icon, iconBg, title, badge, badgeLoading, desc, linkText = "View all", ...linkProps }) => (
  <Link className="action-card" {...linkProps}>
    <div className="ac-icon" style={{ background: iconBg }}><img alt="" src={icon} /></div>
    <div className="ac-head">
      <span className="ac-title">{title}</span>
      {badgeLoading
        ? <span className="ac-badge ac-badge-loading" role="status" aria-label="Loading count" />
        : badge > 0 && <span className="ac-badge">{badge}</span>}
    </div>
    <div className="ac-desc">{desc}</div>
    <div className="ac-link">{linkText} <img alt="" src={VectorIcon} /></div>
  </Link>
);

const Home = () => {
  document.title = "CS Cloud Lab | " + global_var.company_name;

  const isStudent = global_var.role_id === global_var.STUDENT_ROLE_ID;
  const isAdmin = global_var.role_id === global_var.SUPERADMIN_ROLE_ID || global_var.role_id === global_var.ADMIN_ROLE_ID;

  const [tip, setTip] = useState("");
  const [pendingEvaluationCount, setPendingEvaluationCount] = useState(0);
  const [submittedAssignmentCount, setSubmittedAssignmentCount] = useState(0);
  const [notStartedAssignmentCount, setNotStartedAssignmentCount] = useState(0);
  const [countsLoading, setCountsLoading] = useState(true);
  const [openTour, setOpenTour] = useState(false);
  const [projectStacks, setProjectStacks] = useState(() => {
    const cached = getFromCache('cslabMenuData');
    return cached ? buildProjectStacks(cached) : null;
  });

  const goToIdeButton = useRef(null);
  const goToMyProjects = useRef(null);
  const ref3 = useRef(null);
  const [open, setOpen] = useState(false);
  const [tutorialUrl, setTutorialVideo] = useState('');

  // const userToken = localStorage.getItem("token");
  const userToken = sessionStorage.getItem('token');

  const navigate = useNavigate();

  const handleCancel = () => {
    setOpen(false);
    setTutorialVideo('')
  };

  const steps = [
    {
      title: "IDE",
      description: "Click on this button to navigate to IDE",
      cover: <img alt="ide screen" src={tourIDEimg} />,
      placement: "bottom",
      target: () => goToIdeButton.current,
      className: "glass",
    },
    {
      title: "My Projects",
      description: "Click on this button to see your projects",
      cover: <img alt="my projects screen" src={tourProjects} />,
      placement: "bottom",
      target: () => goToMyProjects.current,
      className: "glass",
    },
  ];
  useEffect(() => {
    // Already built synchronously from the cache on first render
    if (getFromCache('cslabMenuData')) return;
    loadMenuForCslab().then(res => {
      setCache('cslabMenuData', res);
      const stacks = buildProjectStacks(res);
      if (stacks) setProjectStacks(stacks);
    });
  }, []);

  // On a fresh mount the cards come from the cache, so this runs before the
  // first paint and the page opens directly at the remembered position.
  // Otherwise it runs once the async menu load has made the page tall enough.
  const scrollRestored = useRef(false);
  useLayoutEffect(() => {
    if (projectStacks && !scrollRestored.current) {
      scrollRestored.current = true;
      window.scrollTo(0, lastHomeScrollY);
    }
  }, [projectStacks]);

  useEffect(() => {
    if (!projectStacks) return;
    const onScroll = () => { lastHomeScrollY = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [projectStacks]);

  async function navigateToIde(projectStackName) {
    trackEvent("landing_page_ide_button_clicked", {
      ide_project_stack_name: projectStackName,
    });
    if (!(await canCreateProject(projectStackName))) { return; }

    const projectMetaData = getIdeProjectTypeMetaData(projectStackName);
    if (projectMetaData.route) {
      if (projectMetaData.openInNewTab) {
        window.open(projectMetaData.route + "?token=" + userToken);
      } else {
        navigate(projectMetaData.route);
      }
    } else {
      message.info("Coming soon.");
    }
  }
  useEffect(() => {
    const isStudent = global_var.role_id === global_var.STUDENT_ROLE_ID;
    const statusFlags = {
      submitted: isStudent ? false : true,
      notStarted: isStudent ? true : false,
      pending: isStudent ? false : true,
    };
    getPendingEvaluationCount(statusFlags).then((res) => {
      const response = res.data;
      if (response.statusCode === 200) {
        setPendingEvaluationCount(response.pendingCount);
        setSubmittedAssignmentCount(response.submittedCount);
        setNotStartedAssignmentCount(response.notStartedCount);
      }
    }).finally(() => setCountsLoading(false));
    getProgrammingTip().then((response) => {
      response = response.data;
      if (response.statusCode === 200) {
        setTip(response.text);
      }
    });
  }, []);


  function renderActions(projectStackName, index, topIndex) {
    const isFirst = index === 0 && topIndex === 0;
    return (
      <div className="tool-actions">
        <span
          role="button"
          className="tool-action"
          id={`new-project-${projectStackName}`}
          data-testid={`new-project-${projectStackName}`}
          onClick={() => navigateToIde(projectStackName)}
          ref={isFirst ? goToIdeButton : null}
        >
          <span className="ta-icon icon-new"><NewIcon /></span>
          <span className="ta-label">New Project</span>
        </span>

        {!getShowMyProjectFlag(projectStackName) && (
          <Link
            className="tool-action"
            id={`my-projects-${projectStackName}`}
            data-testid={`my-projects-${projectStackName}`}
            to={"/projects/" + projectStackName}
            ref={isFirst ? goToMyProjects : null}
            onClick={() => {
              trackEvent("landing_page_projects_button_clicked", {
                ide_project_stack_name: projectStackName,
              });
            }}
          >
            <span className="ta-icon icon-folder"><OpenIcon /></span>
            <span className="ta-label">My Projects</span>
          </Link>
        )}

        {getShowTutorialVideo(projectStackName) && (
          <span
            role="button"
            className="tool-action"
            id={`tutorial-${projectStackName}`}
            data-testid={`tutorial-${projectStackName}`}
            onClick={() => {
              setOpen(true);
              let tutorialVideoUrl = getShowTutorialVideo(projectStackName)
              tutorialVideoUrl = tutorialVideoUrl + '?fs=0&rel=0&modestbranding=0&controls=1&disablekb=1&showinfo=0&iv_load_policy=3&cc_load_policy=0'
              setTutorialVideo(tutorialVideoUrl)
            }}
          >
            <span className="ta-icon icon-video"><PlayIcon /></span>
            <span className="ta-label">Video Tutorial</span>
          </span>
        )}
      </div>
    );
  }


  const greeting = getCurrentGreetingMessage();

  return (
    <div className="cs-home">
      {tutorialUrl !== '' && (
        <Modal
          title="Tutorial"
          open={open}
          footer={null}
          onCancel={handleCancel}
          width="100vw" // Makes the modal width responsive,
          style={{
            top: 20,
            height: "90vh"
          }} // Adjust the top position of the modal
        >
          <div className="modal-iframe-wrapper">
            <iframe
              id="Tutorial"
              src={tutorialUrl}
              title="Scratch JR"
              allow="camera; microphone"
              frameBorder="0"
              style={{
                width: '100%',
                height: '90vh'
              }}
              allowFullScreen
            ></iframe>
          </div>
        </Modal>
      )}

      <div className="greet-row">
        <div className="greet">
          <FontAwesomeIcon
            icon={greeting.icon === "sun" ? faSun : greeting.icon === "cloud-sun" ? faCloudSun : faMoon}
            style={{
              marginRight: '8px',
              color: greeting.icon === "sun" ? "#FDB813" : greeting.icon === "cloud-sun" ? "#F8D775" : "#6E76B4",
            }}
          />
          {greeting.greeting}, <b>{global_var.user_name || global_var.username || <Skeleton />}</b>
        </div>
        <QuestionCircleOutlined
          className="tour-btn"
          ref={ref3}
          onClick={() => {
            trackEvent("landing_page_tour_button_clicked", {});
            setOpenTour(true);
          }}
        />
      </div>
      <div className="greet-sub">Build Something Amazing Today!</div>
      {global_var.platform === 'smart_board' && (
        <Row className="mb-3">
          <Col className="col-12">
            <Alert
              type="info"
              showIcon
              style={{ padding: '4px 12px', fontSize: '13px' }}
              message={
                <>
                  <strong>Not compatible with smart board: </strong>
                  {SMART_BOARD_UNAVAILABLE_FEATURES.join(', ')}.
                </>
              }
            />
          </Col>
        </Row>
      )}

      <div className="action-row">
        {!isStudent && (
          <ActionCard
            to="/evaluateprojects/"
            id="evaluate-projects-card"
            data-testid="evaluate-projects-card"
            onClick={() => trackEvent("landing_page_evaluate_button_clicked", {})}
            icon={EvaluateIcon}
            iconBg="#FEF3C7"
            title="Evaluate Projects"
            badge={pendingEvaluationCount}
            badgeLoading={countsLoading}
            desc="You can evaluate all the projects submitted by students here."
          />
        )}
        <ActionCard
          to={isStudent ? "/labquestionview/" : "/assignments/"}
          id="home-lab-view"
          data-testid="home-lab-view"
          onClick={() => trackEvent("landing_page_lab_asssignment_button_clicked", {})}
          icon={LabAssignmentIcon}
          iconBg="#DBEAFE"
          title="Lab Assignment"
          badge={isStudent ? notStartedAssignmentCount : submittedAssignmentCount}
          badgeLoading={countsLoading}
          desc={isStudent
            ? 'You can see all assignments assigned to you.'
            : ' You can see all lab assignments submitted by students here.'}
        />
        <ActionCard
          to="/portfolio/"
          id="portfolio-card"
          data-testid="portfolio-card"
          onClick={() => trackEvent("landing_page_portfolio_button_clicked", {})}
          icon={PortfolioIcon}
          iconBg="#D1FAE5"
          title={isStudent ? "My Portfolio" : "Student Portfolio"}
          desc={isStudent
            ? "You can see all your projects added to portfolio"
            : "You can see all the projects which are added to portfolio by students."}
        />
        {isAdmin && (
          <ActionCard
            to="/managecslabmenu"
            id="home-manage-cslab-menu"
            data-testid="home-manage-cslab-menu"
            icon={ManageMenuIcon}
            iconBg="#EDE9FE"
            title="Manage CS Lab Menu"
            linkText="Manage"
            desc="You can assign grade-wise menus for each company."
          />
        )}
        {isAdmin && (
          <ActionCard
            to="/manageaifeatures"
            id="home-manage-ai-features"
            data-testid="home-manage-ai-features"
            icon={ChatAiIconBlack}
            iconBg="#FEE2E2"
            title="Manage Ai features"
            linkText="Manage"
            desc="You can enable/disable ai features for students."
          />
        )}
      </div>

      {projectStacks && Object.keys(projectStacks).map((topKey, topIndex) => (
        <div key={topKey} className="tool-section">
          <div className="sec-heading">{categoryDisplayNames[topKey] || topKey}</div>

          {AI_BAND_VARIANTS[topKey] && <AiBand variant={AI_BAND_VARIANTS[topKey]} />}

          <div className="tool-grid">
            {(topKey === "Programming Languages" ? [...projectStacks[topKey], TIP_ITEM] : projectStacks[topKey]).map((projectStack, index) => {
              if (projectStack.isTip) { return <QuickTipCard key="quick-tip" tip={tip} />; }

              const meta = getIdeProjectTypeMetaData(projectStack.projectStackName) || {};
              return (
                <div
                  key={projectStack.projectStackName}
                  className="tool-card"
                  id={`project-card-${projectStack.projectStackName}`}
                  data-testid={`project-card-${projectStack.projectStackName}`}
                >
                  <div className="tool-card-head" style={{ background: getHeadBackground(projectStack.projectStackName, index) }}>
                    <div className="tool-icon"><img alt="" className={ZOOMED_LOGOS[projectStack.projectStackName]} src={getIdeProjectLogo(projectStack.projectStackName)} /></div>
                    <span className="tool-name">
                      {meta.projectStackDisplayName}
                      {meta.beta && <span className="tool-beta">Beta</span>}
                    </span>
                    {meta.aiPowered && <span className="tool-ai-pill">✦ {meta.aiBadge?.desktop || 'AI Powered'}</span>}
                  </div>
                  <div className="tool-card-body">
                    <p>{meta.description}</p>
                    {renderActions(projectStack.projectStackName, index, topIndex)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <Tour
        className="glass"
        open={openTour}
        onClose={() => setOpenTour(false)}
        steps={steps}
      />
    </div>
  );
};

export default Home;
